import {
  init as initSentry,
  captureMessage,
  browserTracingIntegration,
  browserProfilingIntegration,
  makeMultiplexedTransport,
  makeFetchTransport,
  moduleMetadataIntegration,
  logger,
} from "@sentry/browser";
import type { Config } from "../config";
import { BaseConfig } from "./config";

const EXTRA_KEY = "ROUTE_TO";
const transport = makeMultiplexedTransport(makeFetchTransport, (args) => {
  const event = args.getEvent();
  if (
    event &&
    event.extra &&
    EXTRA_KEY in event.extra &&
    Array.isArray(event.extra[EXTRA_KEY])
  ) {
    return event.extra[EXTRA_KEY];
  }
  return [];
});

export const init = (config: Config): Promise<boolean> =>
  new Promise((resolve) => {
    if (!config.SENTRY_DSN) return resolve(false);

    initSentry({
      dsn: config.SENTRY_DSN,
      integrations: [
        browserTracingIntegration(),
        browserProfilingIntegration(),
        moduleMetadataIntegration(),
      ],
      environment: config.SHELL_ENV.CONFIG_NAME ?? "unknown",
      sampleRate: config.SENTRY_ERROR_SAMPLE_RATE ?? 0.05,
      tracesSampleRate: config.SENTRY_PERFORMANCE_SAMPLE_RATE ?? 0.05,
      profilesSampleRate: config.SENTRY_PERFORMANCE_SAMPLE_RATE ?? 0.01,
      parentSpanIsAlwaysRootSpan: false,
      ignoreErrors: [
        ...BaseConfig.ignoreErrors,
        /not-authenticated/,
        /Network error/,
      ],
      denyUrls: [...BaseConfig.denyUrls],
      enableLogs: true,
      attachStacktrace: true,
      transport,
      beforeSend: (event) => {
        if (event?.exception?.values?.[0]?.stacktrace?.frames) {
          const frames = event.exception.values[0].stacktrace.frames;
          // Find the last frame with module metadata containing a DSN
          const routeTo = frames
            .filter(
              (frame) => frame.module_metadata && frame.module_metadata.dsn
            )
            .map((v) => v.module_metadata)
            .slice(-1);

          if (routeTo.length) {
            event.extra = {
              ...event.extra,
              [EXTRA_KEY]: routeTo,
            };
          }
        }
        return event;
      },
    });
  });

export { captureMessage, logger };
